import { type VivaPlanungRouter } from '@backend-types/AppRouter';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import { httpLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import superjson from 'superjson';
import { v7 } from 'uuid';

import { type GetIdTokenFn } from './App.utils.js';
import { deactivatableLoggerLink } from './deactivatableLoggerLink.js';
import { sessionStorageApi, sessionStorageKeys } from './utils';

export const trpc = createTRPCReact<VivaPlanungRouter>();

export function createConfiguredTrpcClient(getIdTokenFunction: GetIdTokenFn, appBackendUrl: string) {
  return trpc.createClient({
    links: [
      deactivatableLoggerLink,
      httpLink({
        transformer: superjson,
        url: appBackendUrl,
        async headers() {
          const adminScopes = sessionStorageApi.get(sessionStorageKeys.X_VIVA_ADMIN_SCOPES);
          const adminScopeHeader = adminScopes === null ? {} : { 'X-Viva-Admin-Scopes': JSON.stringify(adminScopes) };

          const xVivaRequestId = v7();
          sessionStorageApi.set(sessionStorageKeys.LATEST_X_VIVA_REQUEST_ID, xVivaRequestId);

          const authToken = await getIdTokenFunction();
          return {
            Authorization: `Bearer ${authToken}`,
            'X-Viva-Request-Id': xVivaRequestId,
            ...adminScopeHeader,
          };
        },
        async fetch(url, options) {
          const response = await tryToFetch(url, {
            ...options,
            credentials: 'include',
          });

          if (response && response.status !== 403) {
            return response;
          }

          console.warn('[trpc.fetch] Got an access denied response. Retrying with forcibly refreshed token.', {
            response,
          });
          const freshToken = await getIdTokenFunction({ forceRefresh: true });
          const retryHeaders = new Headers(options?.headers);
          retryHeaders.set('Authorization', `Bearer ${freshToken}`);

          return await fetch(url, {
            ...options,
            headers: retryHeaders,
            credentials: 'include',
          });
        },
      }),
    ],
  });
}

async function tryToFetch(url: URL | RequestInfo, options: RequestInit): Promise<Response | undefined> {
  try {
    return await fetch(url, options);
  } catch (error) {
    console.error('[trpc.tryToFetch] Caught exception while trying to fetch', { error });
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Invalidate every query on each mutation, regardless of success, see https://tkdodo.eu/blog/automatic-query-invalidation-after-mutations
    onSettled() {
      void queryClient.invalidateQueries();
    },
  }),
});
